import { useCallback, useEffect, useReducer, useRef } from "react";
import { reducer } from "./BulkCalendarControl.reducer";
import { BulkCalendarControlDateRange } from "./BulkCalendarControl.types";
import type { IBulkCalendarControlAction, IBulkCalendarControlProps, IBulkCalendarControlState } from "./BulkCalendarControl.types";
import { useDictionary } from "../../../../../../../reportng";
import { dictionaryService } from "../../../../../../../reportng/dataFlow";
import { useBulkExportContext } from "../../../../../../providers";
import { useBulkExportFavouriteQueryParams } from "../../../../../../shared/hooks/useBulkExportFavouriteQueryParams";
import { isEqual } from "lodash";

const initialState: IBulkCalendarControlState = {
  lastCalendar: {},
  latestAvailableValue: true,
  availableDateRanges: [],
  selectedRanges: [],
  from: {},
  to: {},
  countLastItem: {},
  items: {
    [BulkCalendarControlDateRange.Annual]: [],
    [BulkCalendarControlDateRange.Quarter]: [],
    [BulkCalendarControlDateRange.Month]: [],
    [BulkCalendarControlDateRange.Week]: [],
    [BulkCalendarControlDateRange.Day]: [],
  },
};

export function useBulkCalendarControl<T extends number | string | unknown = unknown>(props: IBulkCalendarControlProps) {
  const { items: propsItems, availableDateRanges, onChange, dataSourceId, selection, isBulkSubscribtion } = props;
  const { favoriteBulkId } = useBulkExportFavouriteQueryParams();
  //TODO: Убрать данное легаси, заменить на reqct-query
  const [lastCalendarItems] = useDictionary(dictionaryService, "f-last-calendar-bulk", undefined, {
    p_source: Number(dataSourceId),
  });

  const [state, dispatch] = useReducer(reducer, initialState as IBulkCalendarControlState<T>);
  const prevInitItemsPayloadRef = useRef<IBulkCalendarControlAction["payload"] | null>(null);

  const bulkExportContext = useBulkExportContext();
  const tab = bulkExportContext?.state.tab;
  const currentSetting = favoriteBulkId
    ? bulkExportContext?.state.currentFavouriteSettings?.mark.parameters
    : bulkExportContext?.state.currentSavedSettings;

  const currentSavedSettings = bulkExportContext?.state.currentSavedSettings;
  const dimensions = bulkExportContext?.state.dimensions;
  const isBulkFavourite = bulkExportContext?.state?.currentFavouriteSettings?.id;

  useEffect(() => {
    const controlValue = state.selectedRanges.flatMap((dateRange) => {
      const from = state.from[dateRange];
      const to = state.to[dateRange];
      const items = state.items[dateRange];
      const dateRangeValues = items.filter((it) => Number(it.id) >= Number(from) && Number(it.id) <= Number(to)).map((it) => it.id);
      return dateRangeValues as (string | number)[];
    });

    if (controlValue.length) {
      onChange?.(controlValue);
    }
  }, [state]);

  useEffect(
    function initItems() {
      let selected: (string | number)[] | undefined;

      if (selection.length > 0) {
        selected = selection;
      } else if (isBulkSubscribtion) {
        selected = dimensions?.find((el) => el.id === "calendar.default")?.selection || [];
      } else {
        selected = currentSavedSettings?.dimensions.find((el) => el.id === "calendar.default")?.selection || [];
      }

      const payload: IBulkCalendarControlAction["payload"] = {
        value: {
          tab,
          dataSourceId,
          currentSetting,
          availableDateRanges,
          items: propsItems,
          selection: selected,
          isSubscribtion: isBulkSubscribtion,
          isFavourite: isBulkFavourite,
          lastCalendarItems: lastCalendarItems ?? [],
        },
      };

      if (propsItems.length && !isEqual(prevInitItemsPayloadRef.current, payload)) {
        prevInitItemsPayloadRef.current = payload;

        dispatch({
          type: "initItems",
          payload,
        });
      }
    },
    [
      propsItems,
      lastCalendarItems,
      JSON.stringify(availableDateRanges),
      dataSourceId,
      tab,
      isBulkSubscribtion,
      isBulkFavourite,
      currentSavedSettings?.dimensions,
    ]
  );

  const onChangeLatestAvailableValueHander = useCallback((checked: boolean) => {
    dispatch({ type: "setLatestAvailableValue", payload: { value: checked } });
  }, []);

  const onChangeCountLastItemHander = useCallback((dateRange: BulkCalendarControlDateRange, value: number) => {
    dispatch({ type: "changeCountLastItem", payload: { dateRange, value } });
  }, []);

  const onChangeFromHander = useCallback((dateRange: BulkCalendarControlDateRange, value: T) => {
    dispatch({ type: "changeFrom", payload: { dateRange, value } });
  }, []);

  const onChangeToHander = useCallback((dateRange: BulkCalendarControlDateRange, value: T) => {
    dispatch({ type: "changeTo", payload: { dateRange, value } });
  }, []);

  const onChangeCheckedHander = useCallback((dateRange: BulkCalendarControlDateRange, checked: boolean) => {
    dispatch({ type: "toggleDateRange", payload: { dateRange, value: checked } });
  }, []);

  return {
    state: state as IBulkCalendarControlState<T>,
    onChangeCountLastItemHander,
    onChangeFromHander,
    onChangeToHander,
    onChangeCheckedHander,
    onChangeLatestAvailableValueHander,
  };
}
